export const VAT_RATE_SOURCE_MAX_AGE_DAYS = 90

export type VatRate = 0 | 5 | 7 | 10 | 22
export type VatRateKind = 'limited' | 'special_usn' | 'general'
export type VatRateSourceStatus = 'verified' | 'review_required' | 'disabled'

export interface VatRateSource {
  rate: VatRate
  kind: VatRateKind
  effectiveFrom: string
  sourceUrl: string
  sourcePublishedAt: string
  lastVerifiedAt: string
  sourceStatus: VatRateSourceStatus
  statusChangedAt: string
}

const FNS_VAT_SOURCE = 'https://www.nalog.gov.ru/rn77/taxation/taxes/nds/'
const LAW_425_FZ_SOURCE =
  'https://publication.pravo.gov.ru/Document/View/0001202511280017'

const EXPECTED_VAT_RATES = [0, 5, 7, 10, 22] as const satisfies readonly VatRate[]
const VAT_RATE_SOURCE_FIELDS = [
  'effectiveFrom',
  'kind',
  'lastVerifiedAt',
  'rate',
  'sourcePublishedAt',
  'sourceStatus',
  'sourceUrl',
  'statusChangedAt',
] as const
const VAT_RATE_SOURCE_STATUSES = [
  'verified',
  'review_required',
  'disabled',
] as const satisfies readonly VatRateSourceStatus[]
const VAT_RATE_SOURCE_POLICY: Record<VatRate, {
  kind: VatRateKind
  sourceUrl: string
}> = {
  0: { kind: 'limited', sourceUrl: FNS_VAT_SOURCE },
  5: { kind: 'special_usn', sourceUrl: FNS_VAT_SOURCE },
  7: { kind: 'special_usn', sourceUrl: FNS_VAT_SOURCE },
  10: { kind: 'limited', sourceUrl: FNS_VAT_SOURCE },
  22: { kind: 'general', sourceUrl: LAW_425_FZ_SOURCE },
}

export const VAT_RATE_MANIFEST = [
  {
    rate: 0,
    kind: 'limited',
    effectiveFrom: '2026-01-01',
    sourceUrl: FNS_VAT_SOURCE,
    sourcePublishedAt: '2026-01-01',
    lastVerifiedAt: '2026-08-05',
    sourceStatus: 'verified',
    statusChangedAt: '2026-08-05',
  },
  {
    rate: 5,
    kind: 'special_usn',
    effectiveFrom: '2025-01-01',
    sourceUrl: FNS_VAT_SOURCE,
    sourcePublishedAt: '2025-01-01',
    lastVerifiedAt: '2026-08-05',
    sourceStatus: 'verified',
    statusChangedAt: '2026-08-05',
  },
  {
    rate: 7,
    kind: 'special_usn',
    effectiveFrom: '2025-01-01',
    sourceUrl: FNS_VAT_SOURCE,
    sourcePublishedAt: '2025-01-01',
    lastVerifiedAt: '2026-08-05',
    sourceStatus: 'verified',
    statusChangedAt: '2026-08-05',
  },
  {
    rate: 10,
    kind: 'limited',
    effectiveFrom: '2026-01-01',
    sourceUrl: FNS_VAT_SOURCE,
    sourcePublishedAt: '2026-01-01',
    lastVerifiedAt: '2026-08-05',
    sourceStatus: 'verified',
    statusChangedAt: '2026-08-05',
  },
  {
    rate: 22,
    kind: 'general',
    effectiveFrom: '2026-01-01',
    sourceUrl: LAW_425_FZ_SOURCE,
    sourcePublishedAt: '2025-11-28',
    lastVerifiedAt: '2026-08-05',
    sourceStatus: 'verified',
    statusChangedAt: '2026-08-05',
  },
] as const satisfies readonly VatRateSource[]

export type VatRatesUnavailableReason =
  | 'stale'
  | 'review_required'
  | 'disabled'
  | 'invalid_manifest'

export type VatRatesAvailability =
  | { available: true; lastVerifiedAt: string; sourceUrl: string }
  | {
      available: false
      reason: VatRatesUnavailableReason
      lastVerifiedAt?: string
      sourceUrl?: string
    }

const DAY_MS = 24 * 60 * 60 * 1_000

export function getVatRatesAvailability(
  now: Date = new Date(),
  manifest: readonly VatRateSource[] = VAT_RATE_MANIFEST,
): VatRatesAvailability {
  if (
    !Number.isFinite(now.getTime())
    || !isValidVatRateManifest(manifest, startOfUtcDay(now).getTime())
  ) {
    return { available: false, reason: 'invalid_manifest' }
  }

  const firstUnavailable = manifest.find((item) => item.sourceStatus !== 'verified')
  if (firstUnavailable) {
    return {
      available: false,
      reason: firstUnavailable.sourceStatus === 'review_required'
        ? 'review_required'
        : 'disabled',
      lastVerifiedAt: firstUnavailable.lastVerifiedAt,
      sourceUrl: firstUnavailable.sourceUrl,
    }
  }

  let oldest = manifest[0]!
  for (const item of manifest) {
    const verifiedAt = parseIsoDate(item.lastVerifiedAt)
    const oldestAt = parseIsoDate(oldest.lastVerifiedAt)
    if (verifiedAt === null || oldestAt === null) {
      return { available: false, reason: 'invalid_manifest' }
    }
    if (verifiedAt < oldestAt) oldest = item
  }

  const verifiedAt = parseIsoDate(oldest.lastVerifiedAt)
  if (verifiedAt === null) return { available: false, reason: 'invalid_manifest' }
  const ageDays = Math.floor((startOfUtcDay(now).getTime() - verifiedAt) / DAY_MS)
  if (ageDays < 0 || ageDays > VAT_RATE_SOURCE_MAX_AGE_DAYS) {
    return {
      available: false,
      reason: ageDays > VAT_RATE_SOURCE_MAX_AGE_DAYS ? 'stale' : 'invalid_manifest',
      lastVerifiedAt: oldest.lastVerifiedAt,
      sourceUrl: oldest.sourceUrl,
    }
  }

  return {
    available: true,
    lastVerifiedAt: oldest.lastVerifiedAt,
    sourceUrl: FNS_VAT_SOURCE,
  }
}

export function assertVatRateManifestFreshAtBuild(
  now: Date = new Date(),
  manifest: readonly VatRateSource[] = VAT_RATE_MANIFEST,
): void {
  const availability = getVatRatesAvailability(now, manifest)
  if (availability.available) return
  if (availability.reason === 'review_required' || availability.reason === 'disabled') return

  throw new Error(
    `VAT rate manifest cannot be published: ${availability.reason}. `
      + `Verified sources must be no older than ${VAT_RATE_SOURCE_MAX_AGE_DAYS} days.`,
  )
}

export function isVatRate(value: number): value is VatRate {
  return VAT_RATE_MANIFEST.some((item) => item.rate === value)
}

function isValidVatRateManifest(
  manifest: readonly VatRateSource[],
  currentUtcDay: number,
): boolean {
  if (manifest.length !== EXPECTED_VAT_RATES.length) return false

  const seenRates = new Set<VatRate>()
  for (const candidate of manifest as readonly unknown[]) {
    if (!isRecord(candidate)) return false

    const fields = Object.keys(candidate).sort()
    if (
      fields.length !== VAT_RATE_SOURCE_FIELDS.length
      || VAT_RATE_SOURCE_FIELDS.some((field, index) => fields[index] !== field)
    ) {
      return false
    }

    if (
      typeof candidate.rate !== 'number'
      || !EXPECTED_VAT_RATES.includes(candidate.rate as VatRate)
    ) {
      return false
    }
    const rate = candidate.rate as VatRate
    if (seenRates.has(rate)) return false
    seenRates.add(rate)

    const policy = VAT_RATE_SOURCE_POLICY[rate]
    if (candidate.kind !== policy.kind || candidate.sourceUrl !== policy.sourceUrl) {
      return false
    }
    if (
      typeof candidate.sourceStatus !== 'string'
      || !VAT_RATE_SOURCE_STATUSES.includes(candidate.sourceStatus as VatRateSourceStatus)
      || typeof candidate.effectiveFrom !== 'string'
      || typeof candidate.sourcePublishedAt !== 'string'
      || typeof candidate.lastVerifiedAt !== 'string'
      || typeof candidate.statusChangedAt !== 'string'
    ) {
      return false
    }

    const effectiveFrom = parseIsoDate(candidate.effectiveFrom)
    const sourcePublishedAt = parseIsoDate(candidate.sourcePublishedAt)
    const lastVerifiedAt = parseIsoDate(candidate.lastVerifiedAt)
    const statusChangedAt = parseIsoDate(candidate.statusChangedAt)
    if (
      effectiveFrom === null
      || sourcePublishedAt === null
      || lastVerifiedAt === null
      || statusChangedAt === null
      || effectiveFrom > currentUtcDay
      || sourcePublishedAt > currentUtcDay
      || lastVerifiedAt > currentUtcDay
      || statusChangedAt > currentUtcDay
    ) {
      return false
    }

    if (
      sourcePublishedAt > effectiveFrom
      || effectiveFrom > lastVerifiedAt
      || sourcePublishedAt > statusChangedAt
    ) {
      return false
    }
    if (
      candidate.sourceStatus === 'verified'
        ? statusChangedAt > lastVerifiedAt
        : lastVerifiedAt > statusChangedAt
    ) {
      return false
    }
  }

  return EXPECTED_VAT_RATES.every((rate) => seenRates.has(rate))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseIsoDate(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const timestamp = Date.parse(`${value}T00:00:00.000Z`)
  if (!Number.isFinite(timestamp)) return null
  const parsedDate = new Date(timestamp).toISOString().slice(0, 10)
  return parsedDate === value ? timestamp : null
}

function startOfUtcDay(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()))
}
